import { Box } from "@mui/material";
import * as React from "react";
import { v4 as uuidv4 } from "uuid";
import { useKeyPressWithReset } from "../../../../../hooks/useKeyPressWithReset";
import { MeasuringTool } from "../../../../../widgets/Layout/MeasuringTool";
import Canvas from "../../../../../widgets/useCanvas";
import { useLocalRampState, useRampEditorState } from "../../RampEditor/RampEditorPresenter";
import { useSetupWizardStore } from "../components/SetupWizard/useSetupWizardStore";
import { useCanvasControlsStore } from "../stores/useCanvasControlsStore";
import { usePanelState } from "../stores/usePanelState";
import {
  drawCalibrationPointMarkers,
  hitTestCalibrationPoint,
} from "../components/SetupWizard/components/CalibrationPointMarker";

import { useActiveFBOs } from "../../../../../containers/ActiveFBOContainer";
import { useSnackbar } from "../../../../../containers/SnackbarContainer";
import { DraggingScrollContainer } from "../../../../../widgets/DraggingScrollContainer/DraggingScrollContainer";
import { Loading } from "../../../../../widgets/Loading";
import { useReferenceAircraftState } from "../../RampEditor/stores/useReferenceAircraftState";
import { CanvasTextInput } from "./CanvasTextInput";
import { drawMain } from "./drawing";
import { ShapeContextMenu } from "./ShapeContextMenu";
import { GuideLine } from "./spotSnapping";
import { useCanvasHotkeys } from "./EventHandlers/useCanvasHotkeys";
import { useOnCanvasClick } from "./EventHandlers/useOnCanvasClick";
import { useOnContextMenu } from "./EventHandlers/useOnContextMenu";
import { clickedShape, isClickOnReferenceAircraft, useOnMouseDown } from "./EventHandlers/useOnMouseDown";
import { useOnMouseMove } from "./EventHandlers/useOnMouseMove";
import { usePatterns } from "./patterns/usePatterns";
import { SHAPE_COLORS } from "./ShapeEditDialog/constants";
import {
  useTransformHandles,
  useTransformState,
  getBoundingBox,
  getTransformHandles,
  drawTransformOverlay,
  HANDLE_CURSORS,
  isNearVertexPoint,
} from "../TransformHandles";

// Import types from dedicated types file
import {
  Props,
  ContextMenu,
  DraggingPoint,
  DraggingShape,
  Point,
  Shape,
  makeDefaultShape,
} from "./RampCanvasV2.types";

// Re-export types for external consumers
export type {
  Props,
  ContextMenu,
  DraggingPoint,
  DraggingShape,
  Point,
  ReferencePoint,
  SizeCategory,
  ZoneMetadata,
  EgressOption,
  SpotMetadata,
  LineTextPosition,
  LineMetadata,
  Shape,
} from "./RampCanvasV2.types";

export {
  SIZE_CATEGORY_OPTIONS,
  getDefaultSizeCategory,
  getMaxAllowedSizes,
  makeDefaultShape,
} from "./RampCanvasV2.types";

export const RampCanvasV2: React.FC<Props> = ({
  selectedTool,
  setSelectedTool,
  centerOnShapeId,
  onCenterComplete,
}) => {
  const { activeFBO } = useActiveFBOs();
  const snackbar = useSnackbar();
  const isMac = navigator.platform.toUpperCase().indexOf("MAC") >= 0;
  const { ramp, setRamp, highlightedShapes, activeShapes, setActiveShapes } =
    useLocalRampState();

  const [history, setHistory] = React.useState<Shape[][]>([]); // History stack
  const {
    showReferenceImage,
    showMeasurements,
    showGuideLines,
    showCalibrationPoints,
    highlightedCalibrationPoint,
  } = useRampEditorState();

  // Setup wizard state for calibration mode
  const {
    isActive: isSetupWizardActive,
    currentStep,
    pendingPoints,
    addPoint,
    setEditingPoint,
  } = useSetupWizardStore();
  const isCalibrationMode = isSetupWizardActive && currentStep === "calibrate";
  const prevCalibrationMode = React.useRef(isCalibrationMode);
  const [hoveredCalibrationPoint, setHoveredCalibrationPoint] = React.useState<number | null>(null);
  const {
    aircraft: referenceAircraft,
    position: referencePosition,
    loadedImage: referenceImage,
    isSelected: referenceIsSelected,
    setLoadedImage: setReferenceImage,
    setIsSelected: setReferenceIsSelected,
  } = useReferenceAircraftState();
  const [zoomLevel, setZoomLevel] = React.useState<number>(1);
  const [canvasWidth, setCanvasWidth] = React.useState<number>(600);
  const [imageDimensions, setImageDimensions] = React.useState<{width: number, height: number} | null>(null);

  // Load image dimensions when reference_image changes
  // Used to preserve aspect ratio during calibration before GPS dimensions are calculated
  React.useEffect(() => {
    if (!ramp.reference_image) {
      setImageDimensions(null);
      return;
    }
    const img = new Image();
    img.onload = () => {
      setImageDimensions({ width: img.naturalWidth, height: img.naturalHeight });
    };
    img.src = ramp.reference_image;
  }, [ramp.reference_image]);

  // Auto-zoom when entering calibration mode for precise point placement
  React.useEffect(() => {
    if (isCalibrationMode && !prevCalibrationMode.current) {
      // Zoom in to 1.5x for better precision when placing calibration points
      const targetZoom = 1.5;
      setZoomLevel(targetZoom);
      setCanvasWidth((prev) => prev * targetZoom);
    }
    prevCalibrationMode.current = isCalibrationMode;
  }, [isCalibrationMode]);

  const div = React.useCallback((node) => {
    if (node?.offsetWidth) {
      setCanvasWidth(node.offsetWidth * 0.9);
    }
  }, []);

  const canvasRef = React.createRef<HTMLCanvasElement>();
  const scrollContainerRef = React.useRef<HTMLDivElement>(null);

  // Determine if we should use image-based dimensions for aspect ratio
  // Use image dimensions during setup wizard (calibrate + confirm steps) when ramp hasn't been GPS-calibrated yet
  // This prevents the image from being squeezed/distorted
  const useImageAspectRatio = isSetupWizardActive && imageDimensions &&
    (!ramp.calibration_points || ramp.calibration_points.length < 2);

  // Canvas dimensions are fixed to ramp dimensions - never expand based on shape bounds
  // This ensures the ramp image fills the canvas with no whitespace, and GPS calibration points remain stable
  const effectiveWidth = useImageAspectRatio
    ? imageDimensions.width
    : (ramp?.width ?? 1);
  const effectiveDepth = useImageAspectRatio
    ? imageDimensions.height
    : (ramp?.depth ?? 1);

  const pixelsToFeetRatio = canvasWidth / effectiveWidth;
  const canvasHeight = canvasWidth * (effectiveDepth / effectiveWidth);
  const pixelsToFeet = (px: number) => px / pixelsToFeetRatio;

  // Background image size - use ramp dimensions when calibrated (to prevent stretching),
  // full canvas when in setup wizard (to fill canvas during calibration)
  const backgroundImageWidth = useImageAspectRatio ? canvasWidth : (ramp?.width ?? effectiveWidth) * pixelsToFeetRatio;
  const backgroundImageHeight = useImageAspectRatio ? canvasHeight : (ramp?.depth ?? effectiveDepth) * pixelsToFeetRatio;

  // Center on shape when centerOnShapeId changes
  React.useEffect(() => {
    if (!centerOnShapeId || !scrollContainerRef.current || !ramp?.shapes) return;

    const shape = ramp.shapes.find((s) => s.id === centerOnShapeId);
    if (!shape || !shape.points?.length) return;

    // Calculate shape center in feet
    const centerXFeet = shape.points.reduce((sum, p) => sum + p.x, 0) / shape.points.length;
    const centerYFeet = shape.points.reduce((sum, p) => sum + p.y, 0) / shape.points.length;

    // Convert to pixels (accounting for zoom and canvas margin)
    const centerXPixels = centerXFeet * pixelsToFeetRatio + 20; // 20px margin
    const centerYPixels = centerYFeet * pixelsToFeetRatio + 20;

    const container = scrollContainerRef.current;

    // Scroll to center the shape in the viewport
    container.scrollTo({
      left: centerXPixels - container.clientWidth / 2,
      top: centerYPixels - container.clientHeight / 2,
      behavior: "smooth",
    });

    onCenterComplete?.();
  }, [centerOnShapeId, ramp?.shapes, pixelsToFeetRatio, onCenterComplete]);

  const { loading: loadingPatterns, patterns } = usePatterns();

  // Load reference aircraft image when aircraft changes
  React.useEffect(() => {
    if (referenceAircraft?.id) {
      const img = new Image();
      img.crossOrigin = "anonymous";
      img.onload = () => {
        setReferenceImage(img);
      };
      img.onerror = () => {
        console.warn("Failed to load reference aircraft image");
        setReferenceImage(null);
      };
      img.src = `https://static.airplx.com/${referenceAircraft.id}.png`;
    } else {
      setReferenceImage(null);
    }
  }, [referenceAircraft?.id, setReferenceImage]);

  // convert shapes to pixels
  const shapes = React.useMemo(
    () =>
      ramp?.shapes?.map((shape) => ({
        ...shape,
        points: shape.points.map((point) => ({
          x: point.x * pixelsToFeetRatio,
          y: point.y * pixelsToFeetRatio,
        })),
      })) || [],
    [ramp?.shapes, pixelsToFeetRatio]
  );

  // returns active shapes in pixels
  const getActiveShapes = React.useCallback(() => {
    return shapes
      .filter(
        (s) => activeShapes.includes(s.id) || draggingShape?.shapeId === s.id
      )
      .filter((s) => !s.locked);
  }, [shapes, activeShapes]);

  // set a given shape, converting from pixels to feet
  const setShape = React.useCallback(
    (id: string, zShape: Shape) => {
      setRamp((currentRamp) => {
        if (!currentRamp) {
          console.error(
            "[RampCanvas] setShape called but currentRamp is undefined"
          );
          return currentRamp;
        }
        return {
          ...currentRamp,
          shapes: currentRamp.shapes.map((shape) => {
            if (shape.id === id) {
              // zShape is in pixels, convert to feet
              return {
                ...zShape,
                points: zShape.points.map((point) => ({
                  x: point.x / pixelsToFeetRatio,
                  y: point.y / pixelsToFeetRatio,
                })),
              };
            }
            // Other shapes are already in feet, return as-is
            return shape;
          }),
        };
      });
    },
    [setRamp, pixelsToFeetRatio]
  );

  // set shapes, converting from pixels to feet
  const setShapes = React.useCallback(
    (shapesOrUpdater: Shape[] | ((prevShapes: Shape[]) => Shape[])) => {
      setRamp((currentRamp) => {
        if (!currentRamp) {
          console.error(
            "[RampCanvas] setShapes called but currentRamp is undefined"
          );
          return currentRamp;
        }

        const updatedShapes =
          typeof shapesOrUpdater === "function"
            ? shapesOrUpdater(
                currentRamp.shapes.map((shape) => ({
                  ...shape,
                  points: shape.points.map((point) => ({
                    x: point.x * pixelsToFeetRatio,
                    y: point.y * pixelsToFeetRatio,
                  })),
                }))
              )
            : shapesOrUpdater;

        return {
          ...currentRamp,
          shapes: updatedShapes.map((shape) => ({
            ...shape,
            points: shape.points.map((point) => ({
              x: point.x / pixelsToFeetRatio,
              y: point.y / pixelsToFeetRatio,
            })),
          })),
        };
      });
    },
    [setRamp, pixelsToFeetRatio]
  );

  const duplicateShapes = () => {
    if (activeShapes.length > 0) {
      const updatedShapes = [...shapes];
      getActiveShapes().forEach((shape) => {
        updatedShapes.unshift({
          ...shape,
          id: uuidv4(),
          group_id: null,  // Ungroup duplicated shapes
          points: shape.points.map((point) => ({
            x: point.x + 25,  // 25 pixels offset (shapes are in PIXELS)
            y: point.y + 25,
          })),
        });
      });
      setShapes(updatedShapes);
      setActiveShapes([...activeShapes, updatedShapes[0].id]);
    }
  };

  // Context menu handlers
  const handleContextDuplicate = () => {
    if (!contextMenu) return;

    // Determine which shapes to duplicate - don't rely on async setActiveShapes
    const shapesToDuplicate = activeShapes.includes(contextMenu.shapeId)
      ? getActiveShapes() // Duplicate all selected shapes
      : [shapes.find((s) => s.id === contextMenu.shapeId)].filter(Boolean);

    if (shapesToDuplicate.length > 0) {
      const updatedShapes = [...shapes];
      const newIds: string[] = [];
      shapesToDuplicate.forEach((shape) => {
        const newId = uuidv4();
        newIds.push(newId);
        updatedShapes.unshift({
          ...shape,
          id: newId,
          group_id: null,  // Ungroup duplicated shapes
          points: shape.points.map((point) => ({
            x: point.x + 25,  // 25 pixels offset (shapes are in PIXELS)
            y: point.y + 25,
          })),
        });
      });
      setShapes(updatedShapes);
      setActiveShapes(newIds);
    }
    setContextMenu(null);
  };

  const handleContextDelete = () => {
    // Delete either the selected shapes or just the right-clicked shape
    const shapesToDelete = activeShapes.length > 0 && activeShapes.includes(contextMenu?.shapeId || "")
      ? activeShapes
      : contextMenu?.shapeId
      ? [contextMenu.shapeId]
      : [];

    if (shapesToDelete.length > 0) {
      setShapes((currentShapes) =>
        currentShapes.filter((s) => !shapesToDelete.includes(s.id))
      );
      setActiveShapes([]);
    }
    setContextMenu(null);
  };

  const handleContextToggleLock = () => {
    if (!contextMenu) return;

    // Get shapes to toggle - either selected shapes or just the right-clicked shape
    const shapesToToggle = activeShapes.length > 0 && activeShapes.includes(contextMenu.shapeId)
      ? activeShapes
      : [contextMenu.shapeId];

    // Check if ANY of the shapes are unlocked - if so, lock all; otherwise unlock all
    const targetShapes = shapes.filter((s) => shapesToToggle.includes(s.id));
    const anyUnlocked = targetShapes.some((s) => !s.locked);

    setShapes((currentShapes) =>
      currentShapes.map((s) =>
        shapesToToggle.includes(s.id) ? { ...s, locked: anyUnlocked } : s
      )
    );
    setContextMenu(null);
  };

  const handleContextOpenProperties = () => {
    requestPropertiesTab();
    setContextMenu(null);
  };

  // Clipboard state for copy/paste
  const [clipboard, setClipboard] = React.useState<Shape[] | null>(null);
  const lastMousePositionRef = React.useRef<{ x: number; y: number } | null>(null);

  // Copy selected shapes to clipboard
  const copyShapes = React.useCallback(() => {
    const shapesToCopy = getActiveShapes();
    if (shapesToCopy.length > 0) {
      setClipboard(JSON.parse(JSON.stringify(shapesToCopy)));
      const shortcut = isMac ? "⌘V" : "Ctrl+V";
      snackbar.notify({
        text: `Copied ${shapesToCopy.length} shape${shapesToCopy.length > 1 ? "s" : ""} — ${shortcut} to paste`,
        severity: "info",
        duration: 2000,
      });
    }
  }, [getActiveShapes, isMac, snackbar]);

  // Copy from context menu (either selected or right-clicked shape)
  const handleContextCopy = () => {
    if (!contextMenu) return;

    const shapesToCopy = activeShapes.includes(contextMenu.shapeId)
      ? getActiveShapes()
      : [shapes.find((s) => s.id === contextMenu.shapeId)].filter(Boolean) as Shape[];

    if (shapesToCopy.length > 0) {
      setClipboard(JSON.parse(JSON.stringify(shapesToCopy)));
      const shortcut = isMac ? "⌘V" : "Ctrl+V";
      snackbar.notify({
        text: `Copied ${shapesToCopy.length} shape${shapesToCopy.length > 1 ? "s" : ""} — ${shortcut} to paste`,
        severity: "info",
        duration: 2000,
      });
    }
    setContextMenu(null);
  };

  // Paste shapes at the specified position (in feet)
  const pasteShapes = React.useCallback((positionFeet?: { x: number; y: number }) => {
    if (!clipboard || clipboard.length === 0) return;

    // Calculate the center of copied shapes
    const allPoints = clipboard.flatMap((s) => s.points);
    const centerX = allPoints.reduce((sum, p) => sum + p.x, 0) / allPoints.length;
    const centerY = allPoints.reduce((sum, p) => sum + p.y, 0) / allPoints.length;

    // Determine paste position - use provided position or last mouse position or offset from center
    let targetX: number;
    let targetY: number;

    if (positionFeet) {
      targetX = positionFeet.x;
      targetY = positionFeet.y;
    } else if (lastMousePositionRef.current) {
      targetX = lastMousePositionRef.current.x;  // Already in PIXELS
      targetY = lastMousePositionRef.current.y;
    } else {
      // Fallback: offset from current position
      targetX = centerX + 25;
      targetY = centerY + 25;
    }

    const offsetX = targetX - centerX;
    const offsetY = targetY - centerY;

    const newShapes: Shape[] = clipboard.map((shape) => ({
      ...shape,
      id: uuidv4(),
      ramp_id: ramp.id,
      fbo_id: ramp.fbo_id,
      points: shape.points.map((point) => ({
        x: point.x + offsetX,
        y: point.y + offsetY,
      })),
    }));

    setShapes((current) => [...newShapes, ...current]);
    setActiveShapes(newShapes.map((s) => s.id));
  }, [clipboard, pixelsToFeetRatio, ramp.id, ramp.fbo_id, setShapes, setActiveShapes]);

  // Undo the last action
  const undo = React.useCallback(() => {
    if (history.length > 0) {
      const previousState = history[history.length - 1]; // Get the last saved state
      if (!previousState) {
        return;
      }
      setShapes(previousState); // Revert to the last state
      setHistory((prevHistory) => prevHistory.slice(0, -1)); // Remove the last state from history
    }
  }, [history, setShapes]);

  const [currentShape, setCurrentShape] = React.useState<Shape>(
    makeDefaultShape(ramp.fbo_id, ramp.id)
  );
  const [draggingPoint, setDraggingPoint] =
    React.useState<DraggingPoint | null>(null);
  const [draggingShape, setDraggingShape] =
    React.useState<DraggingShape | null>(null);
  const [mousePosition, setMousePosition] = React.useState<Point | null>(null);
  const [contextMenu, setContextMenu] = React.useState<ContextMenu | null>(
    null
  );
  const [previewSpots, setPreviewSpots] = React.useState<Shape[]>([]);
  const [snapGuideLines, setSnapGuideLines] = React.useState<GuideLine[]>([]);
  const [textPrompt, setTextPrompt] = React.useState<{
    open: boolean;
    x?: number;
    y?: number;
  }>({
    open: false,
  });
  const [cursor, setCursor] = React.useState<string | null>(null);

  // Get lock status for context menu display
  const contextMenuShape = contextMenu
    ? shapes.find((s) => s.id === contextMenu.shapeId)
    : null;
  const isContextMenuShapeLocked = contextMenuShape?.locked ?? false;

  const isShiftPressed = useKeyPressWithReset("Shift");
  const isCommandPressed = useKeyPressWithReset("Meta");
  const isOptPressed = useKeyPressWithReset("Alt");

  // Transform handles for resize/rotate
  const { hoveredHandle } = useTransformState();
  const {
    handleMouseMove: transformMouseMove,
    handleMouseDown: transformMouseDown,
    handleMouseUp: transformMouseUp,
    isTransforming,
  } = useTransformHandles({
    shapes,
    setShapes,
    activeShapes,
    zoomLevel,
    canvasRef,
    pixelsToFeetRatio,
    ramp,
  });

  // Zoom callbacks for ViewControlsPanel
  const zoomIn = React.useCallback(() => {
    if (zoomLevel < 2.5) {
      setZoomLevel(zoomLevel * 1.1);
      setCanvasWidth(canvasWidth * 1.1);
      setCurrentShape((prev) => ({
        ...prev,
        points: prev.points.map((point) => ({
          x: point.x * 1.1,
          y: point.y * 1.1,
        })),
      }));
    }
  }, [zoomLevel, canvasWidth]);

  const zoomOut = React.useCallback(() => {
    setZoomLevel(zoomLevel * 0.9);
    setCanvasWidth(canvasWidth * 0.9);
    setCurrentShape((prev) => ({
      ...prev,
      points: prev.points.map((point) => ({
        x: point.x * 0.9,
        y: point.y * 0.9,
      })),
    }));
  }, [zoomLevel, canvasWidth]);

  // Use refs to always have latest callbacks without triggering effect re-runs
  // This prevents infinite loops from callback recreation causing Zustand updates
  const undoRef = React.useRef(undo);
  const zoomInRef = React.useRef(zoomIn);
  const zoomOutRef = React.useRef(zoomOut);

  // Keep refs updated when callbacks change
  React.useEffect(() => { undoRef.current = undo; }, [undo]);
  React.useEffect(() => { zoomInRef.current = zoomIn; }, [zoomIn]);
  React.useEffect(() => { zoomOutRef.current = zoomOut; }, [zoomOut]);

  // Register canvas control callbacks for ViewControlsPanel (once on mount)
  const { registerCallbacks, clearCallbacks, setZoomLevel: setStoreZoomLevel } = useCanvasControlsStore();
  const { requestPropertiesTab } = usePanelState();

  React.useEffect(() => {
    registerCallbacks({
      onUndo: () => undoRef.current(),
      onZoomIn: () => zoomInRef.current(),
      onZoomOut: () => zoomOutRef.current(),
    });
    return () => clearCallbacks();
    // Only run on mount/unmount - refs keep callbacks current
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Sync zoom level to store for display purposes
  React.useEffect(() => {
    setStoreZoomLevel(zoomLevel);
  }, [zoomLevel, setStoreZoomLevel]);

  useCanvasHotkeys({
    activeShapes,
    setActiveShapes,
    getActiveShapes,
    pixelsToFeetRatio,
    shapes,
    setShapes,
    undo,
    duplicateShapes,
    setSelectedTool,
    copyShapes,
    pasteShapes: () => pasteShapes(),
    zoomIn,
    zoomOut,
  });

  const draw = React.useCallback(
    (context: CanvasRenderingContext2D) => {
      drawMain(context, {
        shapes,
        currentShape,
        highlightedShapes,
        mousePosition,
        calibrationPoints: ramp?.calibration_points,
        showCalibrationPoints,
        highlightedCalibrationPoint,
        canvasRef,
        pixelsToFeet,
        showMeasurements,
        showGuideLines,
        pixelsToFeetRatio,
        activeShapes,
        patterns,
        isShiftPressed,
        zoomLevel,
        isMetric: activeFBO?.settings?.useMetricSystem || false,
        previewSpots,
        snapGuideLines,
        rampDepth: ramp?.depth,
        referenceAircraft: referenceAircraft
          ? {
              aircraft: referenceAircraft,
              position: referencePosition,
              loadedImage: referenceImage,
              isSelected: referenceIsSelected,
            }
          : undefined,
      });

      // Draw transform handles for selected shapes
      if (activeShapes.length > 0 && selectedTool === "Select") {
        const selectedShapes = shapes.filter(
          (s) => activeShapes.includes(s.id) && !s.locked
        );
        if (selectedShapes.length > 0) {
          // Draw axis-aligned bounding box with transform handles
          const bounds = getBoundingBox(selectedShapes);
          if (bounds) {
            const handles = getTransformHandles(bounds, zoomLevel);
            drawTransformOverlay(context, bounds, handles, hoveredHandle, zoomLevel);
          }
        }
      }

      // Draw calibration point markers during setup wizard (both calibrate and confirm steps)
      if (isSetupWizardActive && pendingPoints.length > 0) {
        const canvas = canvasRef.current;
        if (canvas) {
          drawCalibrationPointMarkers(
            context,
            pendingPoints,
            canvas.width,
            canvas.height,
            hoveredCalibrationPoint ?? undefined
          );
        }
      }
    },
    [
      shapes,
      currentShape,
      previewSpots,
      snapGuideLines,
      activeShapes,
      mousePosition,
      isShiftPressed,
      highlightedShapes,
      canvasRef,
      pixelsToFeet,
      showMeasurements,
      showGuideLines,
      showCalibrationPoints,
      ramp?.calibration_points,
      highlightedCalibrationPoint,
      pixelsToFeetRatio,
      patterns,
      ramp?.depth,
      referenceAircraft,
      referencePosition,
      referenceImage,
      referenceIsSelected,
      selectedTool,
      hoveredHandle,
      zoomLevel,
      isCalibrationMode,
      pendingPoints,
      hoveredCalibrationPoint,
    ]
  );

  React.useEffect(() => {
    const canvas = canvasRef.current;
    const context = canvas?.getContext("2d");
    if (canvas && context) {
      draw(context);
    }
  }, [canvasRef, draw]);

  // Simple check to prevent "undefined" in image URLs
  const safeImageUrl = React.useMemo(() => {
    if (
      ramp.reference_image &&
      typeof ramp.reference_image === "string" &&
      ramp.reference_image.includes("undefined")
    ) {
      console.warn(
        `Invalid URL with 'undefined' detected: ${ramp.reference_image}`
      );
      return null;
    }
    return ramp.reference_image;
  }, [ramp.reference_image]);

  // Add a point to the current shape or complete the shape if clicking on the first point
  const handleCanvasClickBase = useOnCanvasClick({
    selectedTool,
    shapes,
    setShapes,
    currentShape,
    setCurrentShape,
    setMousePosition,
    setSelectedTool,
    ramp,
    activeShapes,
    setActiveShapes,
    canvasRef,
    isCommandPressed,
    isOptPressed,
    isShiftPressed,
    setShape,
    setContextMenu,
    pixelsToFeetRatio,
  });

  // Wrapper for canvas click that handles calibration mode
  const handleCanvasClick = React.useCallback(
    (event: React.MouseEvent) => {
      // In calibration mode, handle clicks for placing calibration points
      if (isCalibrationMode) {
        const canvas = canvasRef.current;
        if (!canvas) return;

        const rect = canvas.getBoundingClientRect();
        const clickX = (event.clientX - rect.left) * (canvas.width / rect.width);
        const clickY = (event.clientY - rect.top) * (canvas.height / rect.height);

        // Check if clicking on an existing point
        const hitIndex = hitTestCalibrationPoint(
          pendingPoints,
          clickX,
          clickY,
          canvas.width,
          canvas.height
        );

        if (hitIndex >= 0) {
          // Clicked on existing point - open GPS entry for it
          setEditingPoint(hitIndex, { x: event.clientX, y: event.clientY });
        } else if (pendingPoints.length < 3) {
          // Clicked on empty space - add new point
          // Convert to relative coordinates (0-1)
          const relativeX = clickX / canvas.width;
          const relativeY = clickY / canvas.height;
          addPoint(relativeX, relativeY);
          // Open GPS entry for the new point
          setEditingPoint(pendingPoints.length, { x: event.clientX, y: event.clientY });
        }
        return;
      }

      // Normal mode - use regular click handler
      handleCanvasClickBase(event);
    },
    [
      isCalibrationMode,
      canvasRef,
      pendingPoints,
      addPoint,
      setEditingPoint,
      handleCanvasClickBase,
    ]
  );

  // Start dragging a point (either from existing shapes or the current shape)
  const handleMouseDown = useOnMouseDown({
    selectedTool,
    setSelectedTool,
    shapes,
    setShapes,
    currentShape,
    setDraggingPoint,
    setDraggingShape,
    draggingPoint,
    draggingShape,
    canvasRef,
    ramp,
    setHistory,
    setTextPrompt,
    pixelsToFeetRatio,
    setActiveShapes,
    activeShapes,
  });

  // Drag the point
  const handleMouseMove = useOnMouseMove({
    selectedTool,
    shapes,
    setShapes,
    currentShape,
    setDraggingPoint,
    setDraggingShape,
    draggingPoint,
    draggingShape,
    canvasRef,
    ramp,
    isShiftPressed,
    setMousePosition,
    getActiveShapes,
    setSelectedTool,
    pixelsToFeetRatio,
    setSnapGuideLines,
  });

  const handleContextMenu = useOnContextMenu({
    selectedTool,
    shapes,
    setContextMenu,
    canvasRef,
  });

  const { setIsDragging: setReferenceIsDragging } = useReferenceAircraftState();

  // Wrapper for mouse down that checks transform handles first
  const handleMouseDownWrapper = React.useCallback(
    (event: React.MouseEvent) => {
      if (selectedTool === "Select") {
        const canvas = canvasRef.current;
        if (!canvas) return;
        const rect = canvas.getBoundingClientRect();
        const mousePos = {
          x: (event.clientX - rect.left) * (canvas.width / rect.width),
          y: (event.clientY - rect.top) * (canvas.height / rect.height),
        };

        // Get selected shapes for vertex check
        const selectedShapes = shapes.filter((s) => activeShapes.includes(s.id));

        // If near a vertex point of a selected shape, skip transform handle check
        // This gives vertex editing priority over transform handles
        if (!isNearVertexPoint(mousePos, selectedShapes)) {
          // If transform handle was clicked, don't pass to regular handler
          if (transformMouseDown(event, mousePos)) {
            return;
          }
        }
      }
      handleMouseDown(event);
    },
    [selectedTool, canvasRef, transformMouseDown, handleMouseDown, shapes, activeShapes]
  );

  // Wrapper for mouse move that checks transform handles first
  const handleMouseMoveWrapper = React.useCallback(
    (event: React.MouseEvent) => {
      const canvas = canvasRef.current;
      if (!canvas) return;
      const rect = canvas.getBoundingClientRect();
      const mousePos = {
        x: (event.clientX - rect.left) * (canvas.width / rect.width),
        y: (event.clientY - rect.top) * (canvas.height / rect.height),
      };

      // Check transform handles (for hover state and active transform)
      if (selectedTool === "Select") {
        const handled = transformMouseMove(event, mousePos);
        if (isTransforming) {
          // If actively transforming, don't run other handlers
          return;
        }
      }

      handleMouseMove(event);
    },
    [selectedTool, canvasRef, transformMouseMove, isTransforming, handleMouseMove]
  );

  // Finish dragging
  const handleMouseUp = () => {
    transformMouseUp();
    setDraggingShape(null);
    setDraggingPoint(null);
    setReferenceIsDragging(false);
    setSnapGuideLines([]); // Clear snap guide lines
  };

  if (loadingPatterns) {
    return <Loading />;
  }

  return (
    <Box
      sx={{
        position: "relative",
        width: "100%",
        height: "100%",
      }}
    >
      <Box
        ref={div}
        sx={{
          cursor,
          position: "relative",
          overflowX: "scroll",
        }}
      >
        <DraggingScrollContainer
          ref={scrollContainerRef}
          zoomLevel={zoomLevel}
          feetToPixels={1 / pixelsToFeetRatio}
          disabled={false}
          onClick={handleCanvasClick}
          onMouseDown={handleMouseDownWrapper}
          onMouseMove={handleMouseMoveWrapper}
          onMouseUp={handleMouseUp}
          onContextMenu={handleContextMenu}
          preventDefault={(event: React.MouseEvent) => {
            if (selectedTool === "Select") {
              // Check if clicking on reference aircraft (must check hit detection, not just isSelected,
              // because isSelected only becomes true AFTER mouseDown runs)
              if (referenceAircraft) {
                const rect = canvasRef.current?.getBoundingClientRect();
                if (rect) {
                  const x = event.clientX - rect.left;
                  const y = event.clientY - rect.top;
                  const refPositionPixels = {
                    x: referencePosition.x * pixelsToFeetRatio,
                    y: referencePosition.y * pixelsToFeetRatio,
                    angle: referencePosition.angle,
                  };
                  if (isClickOnReferenceAircraft(x, y, referenceAircraft, refPositionPixels, pixelsToFeetRatio)) {
                    return true; // Prevent scroll dragging - let reference aircraft be dragged
                  }
                }
              }
              return clickedShape(canvasRef, event, shapes) || hoveredHandle !== null;
            }
            return true;
          }}
        >
          <MeasuringTool
            pixelsToFeetRatio={pixelsToFeetRatio}
            overlayExpansionRatio={1}
          >
            <Canvas
              ref={canvasRef as React.RefObject<HTMLCanvasElement>}
              draw={draw}
              width={canvasWidth}
              height={canvasHeight}
              onClick={handleCanvasClick}
              onMouseDown={handleMouseDownWrapper}
              onMouseMove={(evt) => {
                // Track mouse position for paste operations
                if (canvasRef.current) {
                  const rect = canvasRef.current.getBoundingClientRect();
                  const x = (evt.clientX - rect.left) * (canvasRef.current.width / rect.width);
                  const y = (evt.clientY - rect.top) * (canvasRef.current.height / rect.height);
                  lastMousePositionRef.current = { x, y };

                  // In calibration mode, detect hover over calibration points
                  if (isCalibrationMode) {
                    const hitIndex = hitTestCalibrationPoint(pendingPoints, x, y, canvasRef.current.width, canvasRef.current.height);
                    setHoveredCalibrationPoint(hitIndex >= 0 ? hitIndex : null);
                  }
                }

                if (clickedShape(canvasRef, evt, shapes)) {
                  setCursor("default");
                } else if (hoveredHandle) {
                  setCursor(HANDLE_CURSORS[hoveredHandle]);
                } else {
                  setCursor(null);
                }
                handleMouseMoveWrapper(evt);
              }}
              onMouseUp={handleMouseUp}
              onContextMenu={handleContextMenu} // Attach the right-click handler
              style={{
                border: "1px solid black",
                cursor: isCalibrationMode
                  ? (hoveredCalibrationPoint !== null ? "pointer" : "crosshair")
                  : hoveredHandle
                  ? HANDLE_CURSORS[hoveredHandle]
                  : selectedTool === "Select"
                  ? cursor
                  : "crosshair",
                backgroundImage:
                  showReferenceImage && safeImageUrl
                    ? `url(${safeImageUrl})`
                    : undefined,
                backgroundSize: showReferenceImage
                  ? `${backgroundImageWidth}px ${backgroundImageHeight}px`
                  : undefined,
                backgroundPosition: showReferenceImage ? '0 0' : undefined,
                backgroundRepeat: showReferenceImage ? 'no-repeat' : undefined,
                margin: "20px",
              }}
            />
          </MeasuringTool>
        </DraggingScrollContainer>
      </Box>

      <CanvasTextInput
        open={textPrompt.open}
        setOpen={(open) => setTextPrompt({ open })}
        onDone={(text) => {
          const updatedShapes = [...shapes];
          updatedShapes.unshift({
            id: uuidv4(),
            fbo_id: ramp.fbo_id,
            ramp_id: ramp.id,
            tags: [],
            points: [{ x: textPrompt.x, y: textPrompt.y }],
            locked: false,
            type: "Text",
            stroke_style: "white",
            line_style: "solid",
            line_width: 3,
            text,
            font_size: 18,
            reference_image_height: canvasHeight,
          });
          setShapes(updatedShapes);
          setSelectedTool("Select");
        }}
      />

      {/* Right-click context menu for quick shape actions */}
      <ShapeContextMenu
        contextMenu={contextMenu}
        onClose={() => setContextMenu(null)}
        onDuplicate={handleContextDuplicate}
        onCopy={handleContextCopy}
        onDelete={handleContextDelete}
        onToggleLock={handleContextToggleLock}
        onOpenProperties={handleContextOpenProperties}
        isLocked={isContextMenuShapeLocked}
        containerRef={scrollContainerRef}
      />
    </Box>
  );
};
